import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { trpc } from '../lib/trpc';
import { showToast } from '../utils/toast';

export const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const requestResetMutation = trpc.auth.requestPasswordReset.useMutation();

  const validateEmail = (email: string): boolean => {
    if (!email) {
      setEmailError('Email is required');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validateEmail(email)) {
      return;
    }

    try {
      const result = await requestResetMutation.mutateAsync({
        email,
      });

      showToast(result.message, 'success');
      setEmailSent(true);
    } catch (error: unknown) {
      if (error instanceof Error) {
        showToast(error.message, 'error');
      } else {
        showToast('Failed to send reset email', 'error');
      }
    }
  };

  // Success view after email is sent
  if (emailSent) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Check Your Email</h1>
            <p className="text-gray-600">
              We've sent password reset instructions to your email
            </p>
          </div>

          <Card variant="elevated" padding="lg">
            <div className="text-center">
              {/* Email Icon */}
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
                <svg
                  className="w-8 h-8 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                If an account exists with <strong>{email}</strong>, you will receive a password reset link.
              </p>

              <div className="space-y-4 mb-6">
                <p className="text-gray-700">
                  Click the link in the email to reset your password.
                </p>
                <p className="text-sm text-gray-600">
                  The link will expire in 1 hour.
                </p>
              </div>

              {/* Resend Button */}
              <Button
                variant="outline"
                size="lg"
                fullWidth
                onClick={() => setEmailSent(false)}
              >
                Try Another Email
              </Button>

              {/* Help Text */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-2">Didn't receive the email?</p>
                <ul className="text-sm text-gray-600 text-left space-y-1">
                  <li>• Check your spam or junk folder</li>
                  <li>• Make sure you entered the correct email address</li>
                  <li>• Wait a few minutes before trying again</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Default view: request reset form
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Reset Your Password</h1>
          <p className="text-gray-600">
            Enter your email address and we'll send you a link to reset your password
          </p>
        </div>

        <Card variant="elevated" padding="lg">
          <div className="space-y-4">
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError('');
              }}
              onBlur={() => validateEmail(email)}
              placeholder="you@example.com"
              error={emailError}
              fullWidth
              required
              disabled={requestResetMutation.isPending}
            />

            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={handleSubmit}
              loading={requestResetMutation.isPending}
              disabled={requestResetMutation.isPending}
            >
              Send Reset Link
            </Button>
          </div>
        </Card>

        {/* Back to Login */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/login')}
            className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};
